import { use, useContext, useEffect, useState } from "react"
import Buttons from "../../../../components/buttons/buttons"
import InputSearch from "../../../../utils/controls/input-search"
import { Link, useLocation, useNavigate } from "react-router-dom";
import './global-search.css'
import { fetchTopThree, getGlobalSearchCount } from "../../services/trmInquiryList";
import PageOverlay from "../../../../components/pageoverlay/PageOverLay";
import { UserContext } from "../../../../store/contexts/UserContextProvider";
import { MetadataContext } from "../../../../store/contexts/MetadataContextProvider";

const formatSearchId = (value: any) => String(value ?? '').padStart(6, '0')

export default function GlobalSearchHeader() {
    const navigate = useNavigate()
    const location = useLocation()
    const { me, userRoles, isUserDetailsLoading } = useContext(UserContext)
    const {statusMetadata}=use(MetadataContext)
    const [searchResults, setSearchResults] = useState<any[]>([])
    const [count, setCount] = useState<any>(0)
    const [searchQuery, setSearchQuery] = useState("")
    const [searchedQuery, setSearchedQuery] = useState("")
    const [showPopup, setShowPopup] = useState(false)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        setShowPopup(false)
    }, [searchQuery])

    function colorCode(statusCode: any) {
        if (statusMetadata?.length) {
            if (Number(statusCode) === 0) {
                return 1;
            }
            return statusMetadata![Number(statusCode)]?.StatusCode
        }
    }

    const shouldShowViewAll = searchResults.some(
        (section) => section.count > section.items.length
    )
    const searchSummary = 'No results to match'

const handleSearch = async () => {
    const value = searchQuery.trim()

    if (!value || isUserDetailsLoading) {
        setShowPopup(false);
        return;
    }

    setSearchedQuery(value);
    setIsLoading(true);

    try {
        const results = await fetchTopThree(value, me, userRoles);
        setSearchResults(results);

        const resultsCount = await getGlobalSearchCount(value, me, userRoles);
        setCount(resultsCount);

        setShowPopup(true);
    } catch (error) {
        console.error("Global search failed:", error);

        setSearchResults([]);
        setCount(0);
        setShowPopup(false);
    } finally {
        // Always executed, whether success or failure
        setIsLoading(false);
    }
};
if (location.pathname && location.pathname.startsWith('/global-search')) return null
    return (
        <>
        {isLoading && <PageOverlay />}
            <div className="global-search-header">
                <InputSearch
                    inputProps={{
                        "aria-label": 'Search VIN / Inquiry ID / WO ID / TMS ID',
                        placeholder: 'Search VIN / Inquiry ID / WO ID / TMS ID',
                        label: 'false',
                        value: searchQuery
                    }}
                    onChange={(e: any) => setSearchQuery(e.target.value.replace(/[^A-Za-z0-9-]/g, ''))}
                    onKeyDown={(e: any) => {
                        if (e.key === "Enter") {
                            handleSearch();
                        }
                    }}
                    onSearchClick={(e: any) => {
                        handleSearch();
                    }}
                    aria-label='Search VIN / Inquiry ID / WO ID / TMS ID'
                    icon='icon-rb-search color-primary font-15'
                    className='w-100 border-radius-2 min-w-400'
                    searchgroup='w-100 d-flex align-items-center border-radius-2 search-icon'
                />
                {showPopup && searchQuery && (<div className="global-search-header-results border-radius-1">
                    <div className="global-search-results-header p-3">
                        <p className="sora-semibold font-14" tabIndex={0} aria-label={searchSummary}>
                            {count === 0
                                ? searchSummary
                                : <>{count} Results for <span className="color-primary">"{searchQuery}"</span></>}
                        </p>
                    </div>
                    <div className="global-search-results-body">
                        {searchResults.map((section) => (
                            <div key={section.title}>
                                <div className="search-header-results px-3 py-2 bgcolor-30">
                                    <p className="sora-semibold font-11 text-color-11" tabIndex={0} aria-label={`Section ${section.title.toUpperCase()} with ${section.count} results`}>
                                        {section.title.toUpperCase()} ({section.count})
                                    </p>
                                </div>

                                {section.items.map((item: any) => (
                                    <div key={`${section.title}-${item.InquiryGUID || item.ID}`} className="search-header-grid px-3 py-1">
                                        <div className="search-header-grid-item row gy-1 border-bottom-1 pb-2 mb-2">
                                            <div className="font-12 text-color-46 col-5" tabIndex={0} aria-label={`ID ${item.ID}`}>
                                                ID : <Link
                                                    to={item?.IsWorkOrder ? `/workorders-detailview/${item?.InquiryGUID}` : `/inquiries-detailview/${item?.InquiryGUID}`}
                                                    state={{
                                                        previouspath: location.pathname,
                                                        returnByHistory: true,
                                                        fromSearch: true,
                                                    }}
                                                    className="color-primary sora-semibold"
                                                    onClick={() => setShowPopup(false)}
                                                >
                                                    {formatSearchId(item.ID)}
                                                </Link>
                                            </div>
                                            <div className="font-12 text-color-46 col-7 d-flex gap-1" tabIndex={0} aria-label={`VIN ${item.VINNumber}`}>
                                                <span className="text-nowrap">VIN :</span> <span className="dark-text sora-semibold text-break"> {item.VINNumber}</span>
                                            </div>
                                            <div className="font-12 text-color-46 col-5" tabIndex={0} aria-label={`TMS ID ${item.TMSID}`}>
                                                TMS ID : <span className="dark-text sora-semibold">{item.TMSID}</span>
                                            </div>
                                            <div className="font-12 text-color-46 col-7" tabIndex={0} aria-label={`Status ${item['Status/StatusTitle'] || (Number(item?.StatusCode) === 0 ? 'Saved' : '--')}`}>
                                                Status : <span status-kpis={colorCode(item?.StatusCode) || 'Saved'} className={`sora-semibold`}>{item['Status/StatusTitle'] || (Number(item?.StatusCode) === 0 ? 'Saved' : '--')}</span>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                  {shouldShowViewAll && (<div className="global-search-results-footer">
                        
                            <Buttons
                            label="View all results"
                            className="btn btn-sm justify-content-center w-100 font-12 color-primary"
                            onClick={() => {
                                setShowPopup(false);
                                navigate(`/global-search?query=${searchedQuery}`, {
                                    state: {
                                        previouspath: location.pathname + location.search
                                    }
                                });
                            }}
                            />
                    </div>
                  )}
                </div>)}
            </div>
        </>
    )
}
